/**
 * Full-screen overlay for the yard map editor.
 *
 * Opens from the Settings > Locations page (or LinkedYards) so users
 * can set up and edit their yard map without navigating away. Uses a
 * portal to render above all other content, similar to ActionDrawer
 * but occupying the full viewport.
 */

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Map, Check } from "lucide-react";
import YardMapView, {
  YardMapErrorBoundary,
  YardUtilizationBar,
} from "@/features/yards/YardMapView";
import Button from "@/components/ui/Button";

interface YardMapOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  yardId: string;
  yardName: string;
}

/**
 * Full-screen overlay wrapping YardMapView. Escape to close,
 * body scroll locked while open, backdrop-dimmed.
 */
export default function YardMapOverlay({
  isOpen,
  onClose,
  yardId,
  yardName,
}: YardMapOverlayProps) {
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px]" />

      {/* Full-screen content panel */}
      <div
        className="fixed inset-3 flex flex-col rounded-[var(--radius-lg)] bg-[var(--surface-base)] border border-[var(--border-default)] shadow-2xl overflow-hidden"
        style={{
          animation: "scaleIn 0.2s var(--ease-smooth) forwards",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)] bg-[var(--surface-base)]">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-interactive-primary)]/10">
              <Map className="h-5 w-5 text-[var(--color-interactive-primary)]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[var(--text-primary)]">
                {yardName}
              </h2>
              <p className="text-xs text-[var(--text-secondary)]">
                Facility Map Editor
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <p className="mr-1 hidden text-xs text-[var(--text-tertiary)] sm:block">
              Changes save automatically
            </p>
            <Button size="sm" onClick={onClose}>
              <Check className="h-4 w-4" /> Done
            </Button>
            <button
              onClick={onClose}
              aria-label="Close editor"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-tertiary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--text-primary)] transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <YardUtilizationBar yardId={yardId} />
          <div className="mt-4">
            <YardMapErrorBoundary onReset={onClose}>
              <YardMapView yardId={yardId} />
            </YardMapErrorBoundary>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
